/**
 * Ad click-id capture (gclid / wbraid / gbraid).
 *
 * Google Ads auto-tagging appends a click id to the landing page URL. Uploading
 * an offline conversion later — e.g. when a trial converts to paid days after
 * the browser is gone — needs that id to attribute the conversion back to the
 * click, so it has to be captured on arrival and carried to the server.
 *
 * The id is stored in a cookie scoped to the registrable domain rather than
 * localStorage, because the ad lands on the marketing site (testdriver.ai) while
 * checkout happens on the console (console.testdriver.ai). localStorage is
 * per-origin and would not survive that hop; a `.testdriver.ai` cookie does.
 *
 * Non-production hosts (fly.dev previews, localhost) don't share a registrable
 * domain between the two apps, so capture still works per-origin there but
 * won't carry across — acceptable, since attribution only matters in prod.
 */
const COOKIE = "td_click_id";
const MAX_AGE_DAYS = 90; // Google's click-to-conversion attribution window.

// Ordered by preference: gclid is the classic click id; wbraid/gbraid replace
// it on iOS/web-to-app traffic where gclid isn't set.
const PARAMS = ["gclid", "wbraid", "gbraid"];

const cookieDomain = () => {
  const host = window.location.hostname;
  // Only set a shared parent-domain cookie where the apps actually share one.
  // Anything else (localhost, *.fly.dev previews) gets a host-only cookie.
  return /(^|\.)testdriver\.ai$/.test(host) ? "; domain=.testdriver.ai" : "";
};

const readCookie = (name) => {
  const match = document.cookie.match(
    new RegExp(`(?:^|; )${name}=([^;]*)`)
  );
  return match ? decodeURIComponent(match[1]) : null;
};

/**
 * Capture a click id from the current URL, if present.
 *
 * First writer wins: a click id already stored is not overwritten, so the
 * campaign that originally acquired the user keeps credit rather than being
 * clobbered by a later organic visit or an internal link.
 *
 * @returns {{type: string, value: string} | null} the stored click id, if any.
 */
export function captureClickId() {
  try {
    const params = new URLSearchParams(window.location.search);
    const existing = getClickId();

    for (const type of PARAMS) {
      const value = params.get(type);
      if (!value) continue;

      // Same click id already recorded — nothing to do.
      if (existing && existing.value === value) return existing;
      // A different click means a new ad click; that campaign gets the credit.
      const payload = `${type}:${value}`;
      document.cookie =
        `${COOKIE}=${encodeURIComponent(payload)}; path=/${cookieDomain()}` +
        `; max-age=${MAX_AGE_DAYS * 24 * 60 * 60}; SameSite=Lax` +
        (window.location.protocol === "https:" ? "; Secure" : "");
      return { type, value };
    }

    return existing;
  } catch (e) {
    // Never let click tracking break page load.
    return null;
  }
}

/**
 * Read the Dub referral click id.
 *
 * The `dub_id` cookie is written by the Dub analytics script (loaded on both
 * the marketing site and the console, see marketing BaseLayout and web
 * index.html) when someone follows a go.testdriver.ai link, and is scoped to
 * `.testdriver.ai` so it survives the hop from the marketing site to the
 * console. Passed through checkout as the subscription's `dubCustomerExternalId`,
 * which is what lets Dub attribute the eventual sale back to the referral click.
 *
 * @returns {string | null} the raw dub_id, if present.
 */
export function getDubClickId() {
  try {
    const raw = readCookie("dub_id");
    return raw ? raw.trim() || null : null;
  } catch (e) {
    return null;
  }
}

// PartnerStack server-to-server click id. A partner link redirects through
// partnerlinks.io, which appends `?ps_xid=<id>` to the landing URL; the id is
// posted back from the API (api/api/helpers/partnerstack-track-signup.js) when
// the visitor signs up, which is what credits the partner. Same shared-domain
// cookie as td_click_id, for the same marketing -> console hop. Mirrored inline
// in marketing/astro/layouts/BaseLayout.astro — keep the two in sync.
const PS_COOKIE = "td_ps_xid";
const PS_MAX_AGE_DAYS = 90; // PartnerStack's default attribution window.

/**
 * Capture the PartnerStack click id from the current URL, if present.
 *
 * Last click wins (PartnerStack attributes last-click), so a new `ps_xid`
 * replaces a stored one.
 */
export function capturePartnerStackClickId() {
  try {
    const value = new URLSearchParams(window.location.search).get("ps_xid");
    if (!value || value === getPartnerStackClickId()) return;
    document.cookie =
      `${PS_COOKIE}=${encodeURIComponent(value)}; path=/${cookieDomain()}` +
      `; max-age=${PS_MAX_AGE_DAYS * 24 * 60 * 60}; SameSite=Lax` +
      (window.location.protocol === "https:" ? "; Secure" : "");
  } catch (e) {
    // Never let click tracking break page load.
  }
}

/**
 * @returns {string | null} the stored PartnerStack `ps_xid`, if any.
 */
export function getPartnerStackClickId() {
  try {
    const raw = readCookie(PS_COOKIE);
    return raw ? raw.trim() || null : null;
  } catch (e) {
    return null;
  }
}

/**
 * @returns {{type: string, value: string} | null}
 */
export function getClickId() {
  try {
    const raw = readCookie(COOKIE);
    if (!raw) return null;
    const idx = raw.indexOf(":");
    if (idx < 1) return null;
    const type = raw.slice(0, idx);
    const value = raw.slice(idx + 1);
    if (!PARAMS.includes(type) || !value) return null;
    return { type, value };
  } catch (e) {
    return null;
  }
}

export default {
  captureClickId,
  capturePartnerStackClickId,
  getClickId,
  getDubClickId,
  getPartnerStackClickId,
};
